import React, { useState, useEffect, useCallback } from 'react';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import {
  isValidDate,
  isAfter,
  now,
  DateInputWithConfirm,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm,
  InputWithConfirm,
} from 'shared-components';
import { InputFieldConfig, IImportData, IDate } from '../../../../types';
import { passportStatusLabel } from './utils';
import { savePI } from '../../../../store/student/actions';
import { createUpdatePIFieldAction } from '../../../../store/student/personalInfo/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { useDispatch } from 'react-redux';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { getTemporalDate } from '@/utilities/validation';

export interface IProps {
  sectionTitle: string;
  complete: boolean;
  currentStep: number;
  steps: number;
  passportNumber?: string;
  passportNumberVisible: boolean;
  passportNumberConfig: InputFieldConfig;
  passportStatus?: string;
  passportStatusVisible: boolean;
  passportStatusConfig: InputFieldConfig;
  passportExpectedDate?: string;
  passportExpectedDateVisible: boolean;
  passportExpectedDateConfig: InputFieldConfig;
  passportExpiryDate?: string;
  passportExpiryDateVisible: boolean;
  passportExpiryDateConfig: InputFieldConfig;
  config: any;
  isPageComplete?: boolean;
  importData: IImportData;
}

const PersonalQPage7: React.FC<IProps> = props => {
  const dispatch = useDispatch();
  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE7, config));
  };

  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any) => {
      dispatch(createUpdatePIFieldAction(fieldName, value, refValue));
    },
    [dispatch],
  );

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  const isPassportImportDataPresent = useCallback(() => {
    return !!props.importData.passportNumber || !!props.importData.passportExpiry;
  }, [props.importData.passportExpiry, props.importData.passportNumber]);

  const getImportApprovedFields = (): Array<Q> => {
    const importApproved = [];
    if (isPassportImportDataPresent() && props.passportStatus === 'Yes') {
      importApproved.push(Q.PASSPORT_STATUS);
    }
    if (props.isPageComplete) {
      if (props.passportStatus === 'Yes' && props.importData.passportNumber === props.passportNumber) {
        importApproved.push(Q.PASSPORT_NUMBER);
      }
      if (props.passportStatus === 'Yes' && props.importData.passportExpiry === props.passportExpiryDate) {
        importApproved.push(Q.PASSPORT_EXPIRY);
      }
    }
    return importApproved;
  };

  const getImportUpdateFields = (): Array<Q> => {
    const importUpdateEnabled = [];
    if (props.isPageComplete) {
      if (isPassportImportDataPresent() && props.passportStatus !== 'Yes') {
        importUpdateEnabled.push(Q.PASSPORT_STATUS);
      }
      if (props.passportNumber !== props.importData.passportNumber && props.passportStatus === 'Yes') {
        importUpdateEnabled.push(Q.PASSPORT_NUMBER);
      }
      if (props.passportExpiryDate !== props.importData.passportExpiry && props.passportStatus === 'Yes') {
        importUpdateEnabled.push(Q.PASSPORT_EXPIRY);
      }
    }
    return importUpdateEnabled;
  };
  const [expectedDate, setExpectedDate] = useState<IDate>(getDateFromString(props.passportExpectedDate));
  const [expectedDateErrorMsg, setExpectedDateErrorMsg] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<IDate>(getDateFromString(props.passportExpiryDate));
  const [expiryDateErrorMsg, setExpiryDateErrorMsg] = useState<string>('');
  const [passportStatus] = useState<string | undefined>(props.passportStatus);
  const [passportNumber] = useState<string | undefined>(props.passportNumber);
  const [prevExpiryDate] = useState<string | undefined>(props.passportExpiryDate);
  const [importApproved, setImportApproved] = useState<string[]>(getImportApprovedFields());
  const [importUpdateEnabled] = useState<string[]>(getImportUpdateFields());
  const [importEdited, setImportEdited] = useState<string[]>([]);

  useEffect(() => {
    // setExpectedDate(getDateFromString(props.passportExpectedDate));
    // setExpiryDate(getDateFromString(props.passportExpiryDate));
    // setPassportStatus(props.passportStatus);
    // setPassportNumber(props.passportNumber);
    // setPrevExpiryDate(props.passportExpiryDate);
    // setImportApproved(getImportApprovedFields());
    // setImportUpdateEnabled(getImportUpdateFields());

    if (isPassportImportDataPresent() && !props.passportStatus) {
      onChange(Q.PASSPORT_STATUS, 'Yes');
      setImportApproved(prevState => [...prevState, Q.PASSPORT_STATUS]);
    }
  }, [isPassportImportDataPresent, onChange, props.passportStatus]);

  const handlePassportClick = (question: any) => {
    onChange(Q.PASSPORT_STATUS, question.char === '1' ? 'Yes' : question.char === '2' ? 'Applied' : 'GoingToApply');
    resetFields();
  };

  const resetFields = () => {
    onChange(Q.PASSPORT_NUMBER, '');
    onChange(Q.PASSPORT_EXPIRY, '');
    onChange(Q.PASSPORT_EXPECTED, '');

    setExpiryDate({
      DD: null,
      MM: null,
      YYYY: null,
    });
    setExpectedDate({
      DD: null,
      MM: null,
      YYYY: null,
    });
    setExpiryDateErrorMsg('');
    setExpectedDateErrorMsg('');
    setImportApproved(prevState => prevState.filter(q => q !== Q.PASSPORT_NUMBER));
    setImportEdited(prevState => prevState.filter(q => q !== Q.PASSPORT_NUMBER));
  };

  const isValidFutureDate = (date: IDate): { isValid: boolean; message?: string } => {
    const { YYYY, DD, MM } = date;
    if (YYYY && DD && MM) {
      if (!isValidDate(date)) {
        return { isValid: false, message: 'Please ensure the date is correct' };
      }
      const temporalDate = getTemporalDate(date);
      if (!temporalDate) {
        return { isValid: false, message: 'Please ensure the date is correct' };
      }
      if (!isAfter(temporalDate, now())) {
        return {
          isValid: false,
          message: 'Passport expected date must be in the future',
        };
      }
      return { isValid: true };
    }
    return { isValid: false, message: '' };
  };

  const handleExpiryDateChange = (importApproved = false) => {
    if (importApproved) {
      onChange(Q.PASSPORT_EXPIRY, props.importData.passportExpiry);
      setExpiryDateErrorMsg('');
      return;
    }
    const { YYYY, DD, MM } = expiryDate;
    if (YYYY && DD && MM) {
      if (isValidDate(expiryDate)) {
        onChange(Q.PASSPORT_EXPIRY, getStringFromDate(expiryDate));
        setExpiryDateErrorMsg('');
      } else {
        onChange(Q.PASSPORT_EXPIRY, '');
        setExpiryDateErrorMsg('Please ensure the date is correct');
      }
    }
  };

  const handleExpectedDateChange = () => {
    const { isValid, message = '' } = isValidFutureDate(expectedDate);
    if (isValid) {
      onChange(Q.PASSPORT_EXPECTED, getStringFromDate(expectedDate));
      setExpectedDateErrorMsg('');
    } else {
      onChange(Q.PASSPORT_EXPECTED, '');
      setExpectedDateErrorMsg(message);
    }
  };

  const isImportEnabled = (target: Q): boolean => {
    switch (target) {
      case Q.PASSPORT_STATUS:
        return props.passportStatusVisible && isPassportImportDataPresent();
      case Q.PASSPORT_NUMBER:
        return props.passportNumberVisible && !!props.importData.passportNumber;
      case Q.PASSPORT_EXPIRY:
        return props.passportExpiryDateVisible && !!props.importData.passportExpiry;
      default:
        return false;
    }
  };

  const isImportUpdateEnabled = (target: Q): boolean => {
    if (!props.isPageComplete) return false;
    switch (target) {
      case Q.PASSPORT_STATUS:
        return !!props.passportStatus && importUpdateEnabled.includes(target);
      case Q.PASSPORT_NUMBER:
        return !!props.passportNumber && importUpdateEnabled.includes(target);
      case Q.PASSPORT_EXPIRY:
        return !!props.passportExpiryDate && importUpdateEnabled.includes(target);
      default:
        return false;
    }
  };

  const isImportComplete = (target: Q): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  };

  const getComplete = (): boolean => {
    const importQuestions = [Q.PASSPORT_STATUS, Q.PASSPORT_NUMBER, Q.PASSPORT_EXPIRY];
    const importComplete = importQuestions.every(q => isImportComplete(q));

    return props.complete && importComplete;
  };

  const handleImportApprove = (target: Q): void => {
    setImportApproved(prevState => [...prevState, target]);
    setImportEdited(prevState => prevState.filter(q => q !== target));
  };

  const handleImportEdit = (target: Q): void => {
    setImportApproved(prevState => prevState.filter(q => q !== target));
    setImportEdited(prevState => [...prevState, target]);
  };

  const handleImportUndo = (target: Q): void => {
    setImportApproved(prevState => prevState.filter(q => q !== target));
    setImportEdited(prevState => prevState.filter(q => q !== target));
  };
  return (
    <QuestionPage
      complete={getComplete()}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={() => onNext(props.config)}
      onBackToTaskList={onBackToTaskList}
      section={props.sectionTitle}
    >
      {props.passportStatusVisible && (
        <RadioOptionsWithConfirm
          key={Q.PASSPORT_STATUS}
          id={Q.PASSPORT_STATUS}
          value={passportStatusLabel(props.passportStatus)}
          label={props.passportStatusConfig?.label}
          onChange={(evt: any) => onChange(Q.PASSPORT_STATUS, evt.currentTarget.value)}
          inputHelperText={props.passportStatusConfig?.helpText}
          onClick={(question: any) => handlePassportClick(question)}
          questions={[
            {
              char: '1',
              label: passportStatusLabel('Yes'),
              selected: props.passportStatus === 'Yes',
            },
            {
              char: '2',
              label: passportStatusLabel('Applied'),
              selected: props.passportStatus === 'Applied',
            },
            {
              char: '3',
              label: passportStatusLabel('GoingToApply'),
              selected: props.passportStatus === 'GoingToApply',
            },
          ]}
          importEnabled={isImportEnabled(Q.PASSPORT_STATUS)}
          importValue={passportStatusLabel(props.passportStatus)}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_STATUS)}
          importApproved={importApproved.includes(Q.PASSPORT_STATUS)}
          importEdited={importEdited.includes(Q.PASSPORT_STATUS)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, 'Yes');
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, isImportUpdateEnabled(Q.PASSPORT_STATUS) ? props.passportStatus : 'Yes');
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_STATUS);
            onChange(Q.PASSPORT_STATUS, isImportUpdateEnabled(Q.PASSPORT_STATUS) ? passportStatus : '');
          }}
        />
      )}

      {props.passportNumberVisible && (
        <InputWithConfirm
          key={Q.PASSPORT_NUMBER}
          id={Q.PASSPORT_NUMBER}
          value={props.passportNumber}
          label={props.passportNumberConfig?.label}
          inputHelperText={props.passportNumberConfig?.helpText}
          onChange={(evt: any) => onChange(Q.PASSPORT_NUMBER, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.PASSPORT_NUMBER)}
          importValue={props.importData.passportNumber}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_NUMBER)}
          importApproved={importApproved.includes(Q.PASSPORT_NUMBER)}
          importEdited={importEdited.includes(Q.PASSPORT_NUMBER)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_NUMBER);
            onChange(Q.PASSPORT_NUMBER, props.importData.passportNumber);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_NUMBER);
            onChange(
              Q.PASSPORT_NUMBER,
              isImportUpdateEnabled(Q.PASSPORT_NUMBER) ? props.passportNumber : props.importData.passportNumber,
            );
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_NUMBER);
            onChange(Q.PASSPORT_NUMBER, isImportUpdateEnabled(Q.PASSPORT_NUMBER) ? passportNumber : '');
          }}
        />
      )}

      {props.passportExpiryDateVisible && (
        <DateInputWithConfirm
          key={Q.PASSPORT_EXPIRY}
          id={Q.PASSPORT_EXPIRY}
          label={props.passportExpiryDateConfig?.label}
          value={expiryDate}
          inputHelperText={props.passportExpiryDateConfig?.label}
          errorText={expiryDateErrorMsg}
          onChangeDD={(evt: any) =>
            setExpiryDate({
              ...expiryDate,
              DD: evt.target.value,
            })
          }
          onChangeMM={(evt: any) =>
            setExpiryDate({
              ...expiryDate,
              MM: evt.target.value,
            })
          }
          onChangeYY={(evt: any) => {
            if (evt.target.value.length === 4) {
              setExpiryDate({
                ...expiryDate,
                YYYY: evt.target.value,
              });
              handleExpiryDateChange();
            } else {
              setExpiryDate({
                ...expiryDate,
                YYYY: evt.target.value,
              });
            }
          }}
          onBlur={() => handleExpiryDateChange()}
          importEnabled={isImportEnabled(Q.PASSPORT_EXPIRY)}
          importValue={props.importData.passportExpiry}
          importUpdateEnabled={isImportUpdateEnabled(Q.PASSPORT_EXPIRY)}
          importApproved={importApproved.includes(Q.PASSPORT_EXPIRY)}
          importEdited={importEdited.includes(Q.PASSPORT_EXPIRY)}
          onImportApprove={() => {
            handleImportApprove(Q.PASSPORT_EXPIRY);
            setExpiryDate(getDateFromString(props.importData.passportExpiry));
            handleExpiryDateChange(true);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.PASSPORT_EXPIRY);
            if (isImportUpdateEnabled(Q.PASSPORT_EXPIRY)) {
              setExpiryDate(expiryDate);
              handleExpiryDateChange();
            } else {
              setExpiryDate(getDateFromString(props.importData.passportExpiry));
              handleExpiryDateChange();
            }
          }}
          onImportUndo={() => {
            handleImportUndo(Q.PASSPORT_EXPIRY);
            if (isImportUpdateEnabled(Q.PASSPORT_EXPIRY)) {
              setExpiryDate(getDateFromString(prevExpiryDate));
              handleExpiryDateChange();
            } else {
              setExpiryDate({
                DD: null,
                MM: null,
                YYYY: null,
              });
            }
          }}
        />
      )}

      {props.passportExpectedDateVisible && (
        <DateInputWithConfirm
          key={Q.PASSPORT_EXPECTED}
          id={Q.PASSPORT_EXPECTED}
          label={props.passportExpectedDateConfig?.label}
          value={expectedDate}
          inputHelperText={props.passportExpectedDateConfig?.helpText}
          errorText={expectedDateErrorMsg}
          onChangeDD={(evt: any) =>
            setExpectedDate({
              ...expectedDate,
              DD: evt.target.value,
            })
          }
          onChangeMM={(evt: any) =>
            setExpectedDate({
              ...expectedDate,
              MM: evt.target.value,
            })
          }
          onChangeYY={(evt: any) => {
            if (evt.target.value.length === 4) {
              setExpectedDate({
                ...expectedDate,
                YYYY: evt.target.value,
              });
              handleExpectedDateChange();
            } else {
              setExpectedDate({
                ...expectedDate,
                YYYY: evt.target.value,
              });
            }
          }}
          onBlur={() => handleExpectedDateChange()}
        />
      )}
    </QuestionPage>
  );
};

export default PersonalQPage7;
